import { createTheme } from '@mui/material/styles';

// Centralized theme configuration with all visual tokens
export const theme = createTheme({
  palette: {
    // Core brand colors
    primary: {
      main: '#34503E',  // Primary Main
      light: '#4F785C', // Primary Light
      dark: '#2C4435',  // Primary Dark

    },
    secondary: {
      main: '#004FF2',    // Secondary Main
      light: '#4481FF',   // Secondary Light
      dark: '#0035A2',    // Secondary Dark (opaque)
    },

    // Feedback
    success: {
      main: '#43A66E',  // Success Main
      light: '#5ECC74', // Success Light
    },
    warning: {
      main: '#FF820E',  // Warning Main
      light: '#FDBB00', // Warning Light
    },
    error: {
      main: '#FB2E00',  // Error Main
      light: '#FFA996', // Error Light
      dark: '#A81F00',  // Error Dark
    },

    // Surface & text
    background: {
      default: '#FAFAFA', // App background
      paper: '#FFFFFF',   // Cards, sheets
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },

    // You can also map your brand "border" into MUI's divider
    divider: '#413C38',

    // You can put extra brand tokens under a custom key
    // and access them via theme.palette.extra?.offline, etc.
    // (TypeScript augmentation example shown below.)
    extra: {
      neutrals: {
        black: '#000000',
        grey: '#B5AFA9',
        white: '#FFFFFF',
      },
    },
  },

  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    // Heading styles with specific sizes matching design specs
    h1: {
      fontSize: '32px',
      fontWeight: 500,
      lineHeight: '40px', // 125%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    h2: {
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: '32px', // 133.333%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    h3: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '28px', // 140%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    h4: {
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '27px', // 150%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    h5: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px', // 150%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    h6: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '22px', // 157.143%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    // Body text styles
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20.02px', // 143%
      letterSpacing: '0em',
      fontStyle: 'normal',
    },
    // Caption and overline styles
    caption: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.033em',
    },
    overline: {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
      lineHeight: 1.66,
      letterSpacing: '0.083em',
      textTransform: 'uppercase',
    },
    // Button text
    button: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.028em',
      textTransform: 'uppercase',
    },
    // Subtitle styles
    subtitle1: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    subtitle2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: 1.57,
      letterSpacing: '0.007em',
    },
  },
  spacing: 8, // Base spacing unit (8px)
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0,0,0,0.1)', // Custom shadow matching UI_CONSTANTS
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 4px 8px rgba(0,0,0,0.1)',
    '0 8px 16px rgba(0,0,0,0.1)',
    '0 16px 32px rgba(0,0,0,0.1)',
    '0 32px 64px rgba(0,0,0,0.1)',
    '0 64px 128px rgba(0,0,0,0.1)',
    '0 128px 256px rgba(0,0,0,0.1)',
    '0 256px 512px rgba(0,0,0,0.1)',
    '0 512px 1024px rgba(0,0,0,0.1)',
    '0 1024px 2048px rgba(0,0,0,0.1)',
    '0 2048px 4096px rgba(0,0,0,0.1)',
    '0 4096px 8192px rgba(0,0,0,0.1)',
    '0 8192px 16384px rgba(0,0,0,0.1)',
    '0 16384px 32768px rgba(0,0,0,0.1)',
    '0 32768px 65536px rgba(0,0,0,0.1)',
    '0 65536px 131072px rgba(0,0,0,0.1)',
    '0 131072px 262144px rgba(0,0,0,0.1)',
    '0 262144px 524288px rgba(0,0,0,0.1)',
    '0 524288px 1048576px rgba(0,0,0,0.1)',
    '0 1048576px 2097152px rgba(0,0,0,0.1)',
    '0 2097152px 4194304px rgba(0,0,0,0.1)',
    '0 4194304px 8388608px rgba(0,0,0,0.1)',
    '0 8388608px 16777216px rgba(0,0,0,0.1)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontSize: '0.875rem', // 14px
          fontWeight: 500,
          lineHeight: 1.75,
          letterSpacing: '0.028em',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    // Ensure consistent typography in form inputs
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '0.875rem', // 14px
          lineHeight: 1.57,
        },
      },
    },
    // Ensure consistent typography in list items
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem', // 14px
          fontWeight: 400,
          lineHeight: 1.57,
        },
      },
    },
  },
});

// Type augmentation for extra palette colors
declare module '@mui/material/styles' {
  interface Palette {
    extra: {
      neutrals: {
        black: string;
        grey: string;
        white: string;
      };
    };
  }

  interface PaletteOptions {
    extra?: {
      neutrals?: {
        black?: string;
        grey?: string;
        white?: string;
      };
    };
  }
}
